import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, Plus, Edit2, Trash2, Eye, EyeOff, Calendar, Users, 
  Loader2, ArrowLeft, Save, X, LayoutDashboard, BookOpen, CalendarCheck
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('blog');
  
  // Blog state
  const [posts, setPosts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [showPostForm, setShowPostForm] = useState(false);
  const [postForm, setPostForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    image_url: '',
    author: 'Chef Raj Kumar',
    published: true
  });
  const [saving, setSaving] = useState(false);
  
  // Bookings state
  const [bookings, setBookings] = useState([]);

  const checkAdminAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/account');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.data.is_admin) {
        toast.error('Admin access required');
        navigate('/account');
        return;
      }
      
      setUser(response.data);
      // Fetch data after auth check
      const token2 = localStorage.getItem('token');
      const [postsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/admin/blog`, { headers: { Authorization: `Bearer ${token2}` } }),
        axios.get(`${API}/admin/bookings`, { headers: { Authorization: `Bearer ${token2}` } })
      ]);
      setPosts(postsRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      toast.error('Please login as admin');
      navigate('/account');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    checkAdminAuth();
  }, [checkAdminAuth]);

  const fetchPosts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/blog`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setPostForm({
      ...postForm,
      title,
      slug: editingPost ? postForm.slug : generateSlug(title)
    });
  };

  const resetForm = () => {
    setPostForm({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      image_url: '',
      author: 'Chef Raj Kumar',
      published: true
    });
    setEditingPost(null);
    setShowPostForm(false);
  };

  const handleEditPost = (post) => {
    setPostForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      image_url: post.image_url || '',
      author: post.author,
      published: post.published !== false
    });
    setEditingPost(post);
    setShowPostForm(true);
  };

  const handleSavePost = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      if (editingPost) {
        await axios.put(`${API}/admin/blog/${editingPost.id}`, postForm, { headers });
        toast.success('Blog post updated successfully');
      } else {
        await axios.post(`${API}/admin/blog`, postForm, { headers });
        toast.success('Blog post created successfully');
      }
      
      await fetchPosts();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/blog/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted');
      await fetchPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
      completed: 'bg-gray-100 text-gray-700',
      pending: 'bg-yellow-100 text-yellow-700'
    };
    return styles[status] || styles.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center" data-testid="admin-page">
        <Loader2 className="w-8 h-8 animate-spin text-malva" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-16 bg-gradient-to-b from-silver-bright to-white" data-testid="admin-page">
      <div className="container-elegant">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <LayoutDashboard className="w-8 h-8 text-malva" />
            <div>
              <h1 className="font-serif text-3xl text-gray-800">Admin Dashboard</h1>
              <p className="text-gray-500">Manage your restaurant content</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={() => navigate('/account')}
            className="text-gray-500"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Account
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card-elegant p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-malva/10 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-malva" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{posts.length}</p>
                <p className="text-gray-500 text-sm">Blog Posts</p>
              </div>
            </div>
          </div>
          <div className="card-elegant p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CalendarCheck className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">
                  {bookings.filter(b => b.status === 'confirmed').length}
                </p>
                <p className="text-gray-500 text-sm">Active Bookings</p>
              </div>
            </div>
          </div>
          <div className="card-elegant p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">
                  {bookings.reduce((sum, b) => sum + (b.guests || 0), 0)}
                </p>
                <p className="text-gray-500 text-sm">Total Guests</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="blog" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Blog Posts
            </TabsTrigger>
            <TabsTrigger value="bookings" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Bookings
            </TabsTrigger>
          </TabsList>

          {/* Blog Tab */}
          <TabsContent value="blog">
            <div className="card-elegant p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-xl text-gray-800">Blog Posts</h2>
                <Button 
                  className="btn-primary"
                  onClick={() => {
                    resetForm();
                    setShowPostForm(true);
                  }}
                  data-testid="new-post-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Post
                </Button>
              </div>

              {/* Post Form */}
              {showPostForm && (
                <div className="border border-malva/20 rounded-lg p-6 mb-6 bg-white" data-testid="post-form">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-serif text-lg text-gray-800">
                      {editingPost ? 'Edit Post' : 'Create New Post'}
                    </h3>
                    <Button variant="ghost" size="sm" onClick={resetForm}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <form onSubmit={handleSavePost} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Title</Label>
                        <Input
                          value={postForm.title}
                          onChange={handleTitleChange}
                          placeholder="Post title"
                          required
                          data-testid="post-title-input"
                        />
                      </div>
                      <div>
                        <Label>Slug (URL)</Label>
                        <Input
                          value={postForm.slug}
                          onChange={(e) => setPostForm({ ...postForm, slug: e.target.value })}
                          placeholder="post-url-slug"
                          required
                          data-testid="post-slug-input"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>Excerpt (Short Description)</Label>
                      <Input
                        value={postForm.excerpt}
                        onChange={(e) => setPostForm({ ...postForm, excerpt: e.target.value })}
                        placeholder="Brief summary of the post"
                        required
                        data-testid="post-excerpt-input"
                      />
                    </div>
                    
                    <div>
                      <Label>Content</Label>
                      <textarea
                        value={postForm.content}
                        onChange={(e) => setPostForm({ ...postForm, content: e.target.value })}
                        placeholder="Full post content..."
                        className="w-full min-h-[200px] px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-malva/20 focus:border-malva"
                        required
                        data-testid="post-content-input"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Image URL</Label>
                        <Input
                          value={postForm.image_url}
                          onChange={(e) => setPostForm({ ...postForm, image_url: e.target.value })}
                          placeholder="https://example.com/image.jpg"
                          data-testid="post-image-input"
                        />
                      </div>
                      <div>
                        <Label>Author</Label>
                        <Input
                          value={postForm.author}
                          onChange={(e) => setPostForm({ ...postForm, author: e.target.value })}
                          placeholder="Author name"
                          data-testid="post-author-input"
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={postForm.published}
                          onChange={(e) => setPostForm({ ...postForm, published: e.target.checked })}
                          className="w-4 h-4 rounded border-gray-300 text-malva focus:ring-malva"
                          data-testid="post-published-checkbox"
                        />
                        <span className="text-gray-700">Published</span>
                      </label>
                    </div>
                    
                    <div className="flex gap-3 pt-4">
                      <Button type="submit" disabled={saving} className="btn-primary" data-testid="save-post-btn">
                        {saving ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        {editingPost ? 'Update Post' : 'Create Post'}
                      </Button>
                      <Button type="button" variant="outline" onClick={resetForm}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              {/* Posts List */}
              {posts.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">No blog posts yet</p>
                  <Button 
                    className="btn-primary"
                    onClick={() => setShowPostForm(true)}
                  >
                    Create Your First Post
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {posts.map((post) => (
                    <div 
                      key={post.id} 
                      className="border border-gray-100 rounded-lg p-4 hover:border-malva/30 transition-colors"
                      data-testid={`post-item-${post.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-medium text-gray-800">{post.title}</h3>
                            {post.published ? (
                              <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                <Eye className="w-3 h-3" /> Published
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                <EyeOff className="w-3 h-3" /> Draft
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 line-clamp-2">{post.excerpt}</p>
                          <p className="text-xs text-gray-400 mt-2">
                            By {post.author} • /{post.slug}
                          </p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEditPost(post)}
                            data-testid={`edit-post-${post.id}`}
                          >
                            <Edit2 className="w-4 h-4 text-gray-500" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeletePost(post.id)}
                            data-testid={`delete-post-${post.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <div className="card-elegant p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-xl text-gray-800">All Reservations</h2>
                <p className="text-sm text-gray-500">{bookings.length} total</p>
              </div>

              {bookings.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No bookings yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="bookings-table">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Guest</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Date & Time</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Party</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Contact</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((booking) => (
                        <tr 
                          key={booking.id} 
                          className="border-b border-gray-50 hover:bg-gray-50/50"
                          data-testid={`booking-row-${booking.id}`}
                        >
                          <td className="py-3 px-4">
                            <p className="font-medium text-gray-800">{booking.guest_name}</p>
                            <p className="text-xs text-gray-400">#{booking.id?.slice(0, 8).toUpperCase()}</p>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-gray-800">{booking.date}</p>
                            <p className="text-sm text-gray-500">{booking.time}</p>
                          </td>
                          <td className="py-3 px-4">
                            <span className="flex items-center gap-1 text-gray-600">
                              <Users className="w-4 h-4" />
                              {booking.guests}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-sm text-gray-600">{booking.email}</p>
                            <p className="text-sm text-gray-500">{booking.phone}</p>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(booking.status)}`}>
                              {booking.status?.charAt(0).toUpperCase() + booking.status?.slice(1)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
